import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface LiquidGlassContextType {
  liquidGlassEnabled: boolean;
  shimmerSpeed: string;
  shimmerDirection: "vertical" | "horizontal";
}

const LiquidGlassContext = createContext<LiquidGlassContextType>({
  liquidGlassEnabled: false,
  shimmerSpeed: "2",
  shimmerDirection: "vertical",
});

export const useLiquidGlass = () => {
  const ctx = useContext(LiquidGlassContext);
  const location = useLocation();
  // Disable shimmer/liquid glass throughout admin area
  if (location.pathname.startsWith("/admin")) {
    return { ...ctx, liquidGlassEnabled: false };
  }
  return ctx;
};

export const LiquidGlassProvider = ({ children }: { children: ReactNode }) => {
  const [liquidGlassEnabled, setLiquidGlassEnabled] = useState(false);
  const [shimmerSpeed, setShimmerSpeed] = useState("2");
  const [shimmerDirection, setShimmerDirection] = useState<"vertical" | "horizontal">("vertical");

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["liquid_glass_enabled", "shimmer_speed", "shimmer_direction"]);
      if (data) {
        data.forEach((s: any) => {
          if (s.key === "liquid_glass_enabled") setLiquidGlassEnabled(s.value === "true");
          if (s.key === "shimmer_speed") setShimmerSpeed(s.value);
          if (s.key === "shimmer_direction") setShimmerDirection(s.value as "vertical" | "horizontal");
        });
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--shimmer-speed", `${shimmerSpeed}s`);
    document.documentElement.style.setProperty("--shimmer-offset", `${parseFloat(shimmerSpeed) / 2}s`);
  }, [shimmerSpeed]);

  return (
    <LiquidGlassContext.Provider value={{ liquidGlassEnabled, shimmerSpeed, shimmerDirection }}>
      {children}
    </LiquidGlassContext.Provider>
  );
};
